import { STATUS_META } from './reconcile.js';
import { billingBrandLabel } from './brandDisplay.js';

const text = (value) => (value === null || value === undefined ? '' : String(value).trim());
const normalized = (value) => text(value).toUpperCase();

export const ALL = 'all';
export const OPERATIONAL_DEFAULT_SORT = 'operationalDefault';

const ACRONYMS = new Set(['BAC', 'CPQ', 'DPE', 'GM', 'ID', 'UI']);
const VARIANCE_DRIVER_LABELS = {
  PRODUCT_SET_MISMATCH: 'Product Mismatch',
  SEC_MISMATCH: 'SEC Mismatch',
  SEC_PRICE_MISAPPLIED: 'SEC Price Mismatch',
  BAC_IN_CPQ_ONLY: 'CPQ Only',
  BAC_IN_GM_ONLY: 'GM Only',
};

export const titleFromCode = (value) => text(value)
  .split(/[_\s-]+/)
  .filter(Boolean)
  .map((part) => {
    const upper = part.toUpperCase();
    if (ACRONYMS.has(upper)) return upper;
    const lower = part.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  })
  .join(' ');

export const varianceStatusLabel = (status) => STATUS_META[status]?.label || titleFromCode(status) || 'Unknown';

export const varianceDriverKey = (row = {}) => normalized(
  row.autoDriver
  || row.auto_driver
  || row.reviewDriver
  || row.review?.category
  || row.varianceDriver?.code
  || row.varianceDriver?.label
  || 'UNCLASSIFIED',
);

export const varianceDriverLabel = (row = {}) => {
  const key = varianceDriverKey(row);
  const labelSource = row.varianceDriver?.label || key;
  const displayKey = normalized(labelSource).replace(/[\s-]+/g, '_');
  const fallbackKey = key.replace(/[\s-]+/g, '_');
  return VARIANCE_DRIVER_LABELS[displayKey]
    || VARIANCE_DRIVER_LABELS[fallbackKey]
    || titleFromCode(labelSource)
    || 'Unclassified';
};

export const enrollmentKey = (row = {}) => {
  const value = text(row.dpeEnrollmentStatusCd || row.dpe_enrollment_status_cd);
  return value || 'N/A';
};

export const brandLabel = (row = {}) => {
  return billingBrandLabel(row);
};

const numberValue = (row, key) => {
  if (key === 'maxAbsDelta') return Math.abs(Number(row.maxAbsDelta ?? row.max_abs_delta ?? 0));
  if (key === 'gmTotal') return Number(row.gmTotal ?? row.gm_total ?? 0);
  if (key === 'cpqTotal') return Number(row.cpqTotal ?? row.cpq_total ?? 0);
  if (key === 'dpeTotal') return Number(row.dpeTotal ?? row.dpe_total ?? 0);
  if (key === 'appSheetTotal') return Number(row.appSheetTotal ?? row.app_sheet_total ?? 0);
  return null;
};

const sortValue = (row, key) => {
  const numeric = numberValue(row, key);
  if (numeric !== null) return numeric;
  if (key === 'status') return varianceStatusLabel(row.status);
  if (key === 'bac') return text(row.bac);
  if (key === 'dealer') return text(row.dealerDisplayName || row.dealerName || row.accountMatchSummary?.displayName);
  if (key === 'source') {
    const driver = varianceDriverKey(row);
    const status = text(row.status).toLowerCase();
    const fileSource = driver === 'BAC_IN_CPQ_ONLY' || status === 'missing-gm'
      ? 'CPQ'
      : driver === 'BAC_IN_GM_ONLY' || status === 'missing-cpq'
        ? 'GM'
        : 'GM CPQ';
    const hasSalesforce = Boolean(row.accountMatchSummary?.displayName || row.accountMatchSummary?.salesforceAccountId);
    return `${fileSource}${hasSalesforce ? ' Salesforce' : ''}`;
  }
  if (key === 'reviewed') return row.review?.reviewed ? 1 : 0;
  if (key === 'brand') return brandLabel(row);
  if (key === 'enrollment') return enrollmentKey(row);
  if (key === 'driver') return varianceDriverLabel(row);
  return text(row[key]);
};

const STATUS_RANK = {
  variance: 0,
  'missing-multiple': 1,
  'missing-gm': 2,
  'missing-cpq': 3,
  'missing-dpe': 4,
  match: 9,
};

const ENROLLMENT_RANK = {
  ENROLLED: 0,
  ACTIVE: 0,
  PENDING: 1,
  PENDING_CANCEL: 2,
  NOT_ENROLLED: 3,
  CANCELLED: 4,
  CANCELED: 4,
  'N/A': 5,
};

export const compareOperationalVarianceRows = (left, right) => {
  const statusDelta = (STATUS_RANK[left.status] ?? 8) - (STATUS_RANK[right.status] ?? 8);
  if (statusDelta) return statusDelta;
  const enrollmentDelta = (ENROLLMENT_RANK[normalized(enrollmentKey(left))] ?? 6)
    - (ENROLLMENT_RANK[normalized(enrollmentKey(right))] ?? 6);
  if (enrollmentDelta) return enrollmentDelta;
  const driverCompare = varianceDriverLabel(left).localeCompare(varianceDriverLabel(right), undefined, { numeric: true, sensitivity: 'base' });
  if (driverCompare) return driverCompare;
  const deltaCompare = Math.abs(Number(right.maxAbsDelta ?? right.max_abs_delta ?? 0))
    - Math.abs(Number(left.maxAbsDelta ?? left.max_abs_delta ?? 0));
  if (deltaCompare) return deltaCompare;
  return String(left.bac || '').localeCompare(String(right.bac || ''), undefined, { numeric: true });
};

export const compareVarianceRows = (sortBy, direction = 'desc') => (left, right) => {
  if (sortBy === OPERATIONAL_DEFAULT_SORT) return compareOperationalVarianceRows(left, right);
  const a = sortValue(left, sortBy);
  const b = sortValue(right, sortBy);
  const modifier = direction === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') {
    if (a !== b) return (a - b) * modifier;
  } else {
    const compared = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    if (compared) return compared * modifier;
  }
  return String(left.bac || '').localeCompare(String(right.bac || ''), undefined, { numeric: true });
};

const optionCounts = (rows, keyFor, labelFor = keyFor) => {
  const byKey = new Map();
  rows.forEach((row) => {
    const key = keyFor(row);
    if (!key) return;
    const current = byKey.get(key) || { value: key, label: labelFor(row), count: 0 };
    current.count += 1;
    byKey.set(key, current);
  });
  return [...byKey.values()].sort((left, right) => String(left.label).localeCompare(String(right.label)));
};

export const varianceSummaryOptions = (rows = []) => ({
  statuses: optionCounts(rows, (row) => row.status || 'unknown', (row) => varianceStatusLabel(row.status)),
  drivers: optionCounts(rows, varianceDriverKey, varianceDriverLabel),
  enrollments: optionCounts(rows, enrollmentKey, enrollmentKey),
});

export const applyVarianceSummaryView = (rows = [], {
  status = ALL,
  driver = ALL,
  enrollment = ALL,
  sortBy = 'maxAbsDelta',
  sortDirection = 'desc',
} = {}) => rows
  .filter((row) => status === ALL || row.status === status)
  .filter((row) => driver === ALL || varianceDriverKey(row) === driver)
  .filter((row) => enrollment === ALL || enrollmentKey(row) === enrollment)
  .slice()
  .sort(compareVarianceRows(sortBy, sortDirection));

export const groupedVarianceRows = (rows = [], groupBy = 'none') => {
  if (groupBy === 'none') return [['', rows]];
  const groupFor = (row) => {
    if (groupBy === 'status') return varianceStatusLabel(row.status);
    if (groupBy === 'driver') return varianceDriverLabel(row);
    if (groupBy === 'enrollment') return `DPE ${enrollmentKey(row)}`;
    if (groupBy === 'brand') return brandLabel(row) || 'No brand';
    return 'Other';
  };
  const groups = rows.reduce((acc, row) => {
    const key = groupFor(row);
    if (!acc.has(key)) acc.set(key, []);
    acc.get(key).push(row);
    return acc;
  }, new Map());
  return [...groups.entries()];
};
